/** Fixed-timestep simulation with variable-rate render. */
export class Loop {
  running = false
  private raf = 0
  private last = 0
  private acc = 0
  private readonly step = 1 / 60
  fps = 0
  uncapped = false
  private fpsAcc = 0
  private fpsFrames = 0
  // Dev-only uncapped pump: a MessageChannel schedules the next frame as a macrotask as fast as the
  // browser allows, bypassing requestAnimationFrame's vsync cadence so the overlay reports TRUE peak
  // throughput. (A web page cannot disable OS vsync; this is the standard unthrottled-benchmark
  // equivalent.) Created LAZILY the first time setUncapped(true) runs, so production — which never
  // wires the dev-only U hotkey — never instantiates the channel/handler (zero production cost).
  private chan: MessageChannel | null = null
  // Bumped on every start()/stop(). The pump message carries the generation it was posted under; a
  // message delivered after a stop()/start() cycle carries a stale generation and is dropped, so a
  // freeze→unfreeze can never leave two concurrent self-sustaining pumps running (double-schedule).
  private gen = 0

  constructor(
    private onFixed: (dt: number) => void,
    private onFrame: (dt: number) => void,
  ) {}

  start(): void {
    if (this.running) return
    this.running = true
    this.gen++ // invalidate any pump message still queued from a previous run
    this.last = performance.now()
    this.schedule()
  }

  stop(): void {
    this.running = false
    this.gen++ // invalidate any in-flight uncapped pump message so it can't resurrect the loop
    cancelAnimationFrame(this.raf)
  }

  /** Dev-only: switch between vsync (requestAnimationFrame) and uncapped (MessageChannel) pacing.
   * Returns the effective state. The pump is created lazily here so production never allocates it. */
  setUncapped(v: boolean): boolean {
    if (v && !this.chan && typeof MessageChannel !== 'undefined') {
      this.chan = new MessageChannel()
      this.chan.port1.onmessage = (e: MessageEvent) => {
        if (e.data !== this.gen) return // stale message from a prior start/stop cycle — drop it
        this.advance(performance.now())
      }
    }
    this.uncapped = v && this.chan !== null
    return this.uncapped
  }

  private schedule(): void {
    if (!this.running) return
    if (this.uncapped && this.chan) this.chan.port2.postMessage(this.gen)
    else this.raf = requestAnimationFrame(this.tick)
  }

  private tick = (now: number): void => {
    this.advance(now)
  }

  private advance(now: number): void {
    if (!this.running) return
    this.schedule() // queue the next frame first (matches the rAF self-reschedule pattern)

    let dt = (now - this.last) / 1000
    this.last = now
    if (dt > 0.25) dt = 0.25 // clamp after tab-away so we don't spiral

    this.acc += dt
    let steps = 0
    while (this.acc >= this.step && steps < 5) {
      this.onFixed(this.step)
      this.acc -= this.step
      steps++
    }
    this.onFrame(dt)

    this.fpsAcc += dt
    this.fpsFrames++
    if (this.fpsAcc >= 0.5) {
      this.fps = Math.round(this.fpsFrames / this.fpsAcc)
      this.fpsAcc = 0
      this.fpsFrames = 0
    }
  }
}
